import { ButcherHeader } from '@components/ButcherHeader';
// import { Assets } from '@helpers/Constants/Assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faEllipsisVertical,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import Pagination from '@components/Pagination';
import { useEffect, useState, useRef } from 'react';
import {
  AllOrderCrumbSteps,
  CustomerModules,
  errorMessage,
  newOrderProcessingStatusFilters,
  OrderProcessingMasterData,
  pagination,
  searchConfig,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { useNavigate } from 'react-router-dom';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { NewOrderInitiationProps, OrderDetails } from '@helpers/Models';
import { CreateOrderModal } from '@components/Modal/OrderProcessing/CreateOrderModal';
import { formatDate, handleDetailsView } from '@services/OrderCreationService';
import { useDismissDropdown } from '@services/DropdownService';
import { CustomerOrderService } from '@services/CustomerService';

type OrderListRow = OrderDetails & { isDraft?: boolean };
type DraftOrderRow = OrderListRow & {
  draftCustomerId: number;
  draftCustomerDetails?: NewOrderInitiationProps;
};

export const AllOrderList: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchKey, setSearchKey] = useState('');
  const [allOrders, setAllOrders] = useState<OrderListRow[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const filterDropdownRef = useRef<HTMLDivElement | null>(null);
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<number | null>(
    null,
  );
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isCreatingNewOrder, setisCreatingNewOrder] = useState(false);
  const [isOrderCreationAllowed, setIsOrderCreationAllowed] =
    useState<boolean>(false);

  // ---------- Sorting state ----------
  const [sortField, setSortField] = useState<string>(''); // e.g. 'orderNumber', 'customerName', 'createdDate', 'status'
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const draftOrders = (() => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();

    return Object.entries(parsedOrderData)
      .filter(([, order]) => Array.isArray(order?.species) && order.species.length > 0)
      .map(([customerId, order]) => {
        const draftCustomerId = Number(customerId);
        const customerDetails =
          order.customerDetails ?? CustomerOrderService.getCustomerDetails();

        return {
          id: draftCustomerId,
          orderNumber: '-',
          ButcherCustomers: {
            id: customerDetails?.id ?? draftCustomerId,
            firstName: customerDetails?.firstName ?? 'Draft',
            lastName: customerDetails?.lastName ?? 'Order',
          },
          createdBy: draftCustomerId,
          status: 0,
          createdByUser: {} as OrderDetails['createdByUser'],
          createdAt: new Date().toISOString(),
          isDraft: true,
          draftCustomerId,
          draftCustomerDetails: customerDetails,
        } as DraftOrderRow;
      });
  })();

  const visibleOrders =
    selectedOrderStatus === null ? [...draftOrders, ...allOrders] : allOrders;

  const selectedOrderStatusLabel =
    newOrderProcessingStatusFilters.orderStatus.find(
      (status) => status.value === selectedOrderStatus,
    )?.label ?? 'All Orders';

  /**
   * Debounces the `searchKey` input to avoid triggering updates too frequently.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, searchConfig.SEARCH_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [searchKey]);

  /**
   * Check whether order creation is allowed (customer or new order module).
   */
  useEffect(() => {
    const modules = localStorage.getItem('modules') ?? '';
    const parsedModules = JSON.parse(modules);
    setIsOrderCreationAllowed(
      parsedModules.every(
        (id: number) =>
          id === CustomerModules.CUSTOMERS || id === CustomerModules.NEW_ORDER,
      ),
    );
  }, []);

  /**
   * Fetches orders whenever pagination, search, status filter, or sorting changes.
   */
  useEffect(() => {
    setisCreatingNewOrder(false);
    fetchAllOrders(
      currentPage,
      debouncedSearchKey,
      itemsPerPage,
      selectedOrderStatus,
      sortField,
      sortDirection,
    );
  }, [currentPage, itemsPerPage, debouncedSearchKey, selectedOrderStatus, sortField, sortDirection]);

  /**
   * Custom hook to dismiss dropdowns on outside click.
   */
  useDismissDropdown({
    refs: [
      ...Array.from(dropdownRefs.current.values()).map((el) => ({
        current: el,
      })),
      { current: filterDropdownRef.current },
    ],
    onClose: () => {
      setActiveDropdown(null);
      setIsFilterDropdownOpen(false);
    },
  });

  /**
   * Fetches a paginated list of orders from the API.
   */
  const fetchAllOrders = async (
    page: number,
    searchKey: string,
    count: number = itemsPerPage,
    status: number | null = selectedOrderStatus,
    field: string = sortField,
    direction: 'asc' | 'desc' = sortDirection,
  ): Promise<void> => {
    try {
      const payload: {
        page: number;
        count: number;
        searchKey: string;
        status?: number;
        sort?: Record<string, 'asc' | 'desc'>;
      } = {
        page,
        count,
        searchKey,
      };
      if (status !== null) payload.status = status;
      if (field) {
        payload.sort = { [field]: direction };
      }

      const response = await Axios.post(ApiEndpoints.ALL_ORDERS, payload);

      const data = response?.data?.data;
      setAllOrders(data.orders ?? []);
      setCurrentPage(data.currentPage || 1);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the active dropdown state for a given order ID.
   */
  const toggleDropdown = (orderId: number) => {
    setActiveDropdown(activeDropdown === orderId ? null : orderId);
  };

  const handleStatusFilterSelect = (status: number | null): void => {
    setSelectedOrderStatus(status);
    setCurrentPage(1);
    setIsFilterDropdownOpen(false);
  };

  const handleResumeDraftOrder = (draftOrder: DraftOrderRow): void => {
    const customerId = draftOrder.draftCustomerId;

    if (draftOrder.draftCustomerDetails) {
      CustomerOrderService.setCustomerDetails(draftOrder.draftCustomerDetails);
    }

    localStorage.setItem('customerId', String(customerId));
    localStorage.setItem('currentCustomizingId', '0');
    setActiveDropdown(null);
    navigate(FrontendRoutes.CREATE_ORDER);
  };

  /**
   * Handles column header click to set sort field and direction.
   */
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // reset to first page when sorting changes
  };

  /**
   * Renders the sort icon for a given column.
   */
  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-300" />;
    }
    if (sortDirection === 'asc') {
      return <FontAwesomeIcon icon={faSortUp} className="ml-1 text-gray-600" />;
    }
    return <FontAwesomeIcon icon={faSortDown} className="ml-1 text-gray-600" />;
  };

  const BreadCrumbsProps = {
    steps: AllOrderCrumbSteps,
    activeItem: 'All Orders',
    type: OrderProcessingMasterData.ALL_ORDER,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={3} />
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="flex-1 flex flex-col overflow-auto p-1">
          <div className="flex justify-between items-center p-3">
            <h1 className="font-semibold text-xl px-5 mt-3">All Orders</h1>
            <div className="flex items-center gap-2">
              <div className="relative" ref={filterDropdownRef}>
                <button
                  type="button"
                  onClick={() =>
                    setIsFilterDropdownOpen((previousState) => !previousState)
                  }
                  className="min-w-[180px] h-10 border border-gray-300 bg-white px-4 rounded text-left text-sm font-medium text-[#1C1B1F] flex items-center justify-between gap-3 hover:border-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <span>{selectedOrderStatusLabel}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-gray-500 transition-transform duration-200 ${isFilterDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isFilterDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    <ul className="py-2">
                      <li className="mx-2 my-0">
                        <button
                          type="button"
                          onClick={() => handleStatusFilterSelect(null)}
                          className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                        >
                          All Orders
                        </button>
                      </li>
                      {newOrderProcessingStatusFilters.orderStatus.map((status) => (
                        <li key={status.value} className="mx-2 my-0">
                          <button
                            type="button"
                            onClick={() => handleStatusFilterSelect(status.value)}
                            className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                          >
                            {status.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  onChange={(e) => setSearchKey(e.target.value)}
                  value={searchKey}
                  className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
              </div>
            </div>
          </div>

          <div className="shadow-lg mt-3 rounded-lg">
            <form>
              <table className="w-full table-auto rounded-xl">
                <thead className="bg-gray-100">
                  <tr className="border-b">
                    {/* Order No. */}
                    <th className="px-4 py-2 text-center font-semibold">
                      <button
                        type="button"
                        onClick={() => handleSort('orderNumber')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Order No.
                        {renderSortIcon('orderNumber')}
                      </button>
                    </th>
                    {/* Customer */}
                    <th className="px-4 py-2 text-center font-semibold">
                      <button
                        type="button"
                        onClick={() => handleSort('customerName')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Customer
                        {renderSortIcon('customerName')}
                      </button>
                    </th>
                    {/* Created On */}
                    {/* Order Status */}
                    <th className="px-4 py-2 text-center font-semibold">
                      <button
                        type="button"
                        onClick={() => handleSort('status')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Order Status
                        {renderSortIcon('status')}
                      </button>
                    </th>
                    <th className="px-4 py-2 text-center font-semibold">
                      Product Status
                    </th>
                    <th className="px-4 py-2 text-center font-semibold">
                      Service Status
                    </th>
                    <th className="px-4 py-2 text-center font-semibold">
                      <button
                        type="button"
                        onClick={() => handleSort('createdDate')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Created On
                        {renderSortIcon('createdDate')}
                      </button>
                    </th>
                    <th className="px-4 py-2 text-center font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.length > 0 ? (
                    visibleOrders.map((order) => (
                      <tr
                        key={`${order.isDraft ? 'draft' : 'order'}-${order.id}`}
                        onClick={() => {
                          if (order.isDraft) return;
                          handleDetailsView(
                            order.id,
                            navigate,
                            FrontendRoutes.ORDER_DETAILS,
                          );
                          setSelectedOrderId(order.id);
                        }}
                        className={`hover:cursor-pointer hover:bg-tertiary hover:border hover:border-primary hover:b-2 ${!order.isDraft && selectedOrderId === order.id
                          ? 'bg-tertiary border border-primary'
                          : ''
                          }`}
                      >
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          {order.orderNumber}
                        </td>
                        <td className=" px-4 py-2 text-center whitespace-nowrap">
                          {order.ButcherCustomers.firstName}{' '}
                          {order.ButcherCustomers.lastName}
                        </td>
                        <td className="flex justify-center items-center whitespace-nowrap px-4 py-2">
                          {order.isDraft ? (
                            <OrderStatusBadge type="draftStatus" status={0} />
                          ) : (
                            <OrderStatusBadge
                              type="orderStatus"
                              status={order.status}
                            />
                          )}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          {order.isDraft || order.productStatus === null || order.productStatus === undefined ? (
                            <div className="flex justify-center">
                              <div className="px-4 py-1 font-semibold rounded-full border-2 w-fit text-center text-gray-500 bg-gray-100 border-gray-300">
                                -
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-center">
                              <OrderStatusBadge
                                type="orderListProductStatus"
                                status={order.productStatus}
                              />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          {order.isDraft || order.serviceStatus === null || order.serviceStatus === undefined ? (
                            <div className="flex justify-center">
                              <div className="px-4 py-1 font-semibold rounded-full border-2 w-fit text-center text-gray-500 bg-gray-100 border-gray-300">
                                -
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-center">
                              <OrderStatusBadge
                                type="orderListServiceStatus"
                                status={order.serviceStatus}
                              />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          {formatDate(order.createdAt)}
                        </td>
                        <td className="px-4 py-2 text-center relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(order.id);
                            }}
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </button>
                          {activeDropdown === order.id && (
                            <div
                              className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10"
                              ref={(el) => {
                                if (el) {
                                  dropdownRefs.current.set(order.id, el);
                                } else {
                                  dropdownRefs.current.delete(order.id);
                                }
                              }}
                            >
                              <ul className="py-2 text-left">
                                {order.isDraft ? (
                                  <li className="mx-2 my-0">
                                    <button
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleResumeDraftOrder(order as DraftOrderRow);
                                      }}
                                      type="button"
                                      className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                    >
                                      Resume
                                    </button>
                                  </li>
                                ) : (
                                  <>
                                    <li className="mx-2 my-0">
                                      <button
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleDetailsView(
                                            order.id,
                                            navigate,
                                            FrontendRoutes.ORDER_DETAILS,
                                          );
                                        }}
                                        type="button"
                                        className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                      >
                                        View order summary
                                      </button>
                                    </li>
                                    <li className="mx-2 my-0">
                                      <button
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleDetailsView(
                                            order.id,
                                            navigate,
                                            FrontendRoutes.SERVICE_PROCESS_TASK_LIST,
                                          );
                                        }}
                                        type="button"
                                        className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                      >
                                        View service processing
                                      </button>
                                    </li>
                                    <li className="mx-2 my-0">
                                      <button
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleDetailsView(
                                            order.id,
                                            navigate,
                                            FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST,
                                          );
                                        }}
                                        type="button"
                                        className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                      >
                                        View product processing
                                      </button>
                                    </li>

                                    <li className="mx-2 my-0">
                                      <button
                                        type="button"
                                        className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                      >
                                        View invoices
                                      </button>
                                    </li>
                                  </>
                                )}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className=" px-4 py-2 text-center">
                        No data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </form>

            {/* Pagination */}
            <div className="bottom-0">
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>

            {/* Button */}
          </div>
        </div>
        <div className="modal-divs">
          {isCreatingNewOrder && (
            <CreateOrderModal
              onClose={() => {
                setisCreatingNewOrder(false);
              }}
              fromList="all_order"
            />
          )}
        </div>
        {/* Display `Create New Order` button only when user have customer or new order module permission */}
        {isOrderCreationAllowed && (
          <div className="mt-auto pt-4 pb-4 pr-2">
            <button
              type="button"
              onClick={() => setisCreatingNewOrder(true)}
              className="w-full px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
            >
              + Create New Order
            </button>
          </div>
        )}
        <div className="modal-divs">
          {isCreatingNewOrder && (
            <CreateOrderModal
              onClose={() => {
                setisCreatingNewOrder(false);
              }}
              fromList="all_order"
            />
          )}
        </div>
      </div>
    </div>
  );
};